import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Crown,
  Building2,
  Handshake,
  Megaphone,
  Heart,
  Users,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  ChevronLeft,
  Send,
  Shield
} from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const API = process.env.REACT_APP_BACKEND_URL;

const INQUIRY_TYPES = [
  {
    id: 'sponsor',
    label: 'Charter Sponsor',
    icon: Crown,
    description: 'Become a founding sponsor',
    color: 'bg-purple-100 text-purple-700'
  },
  {
    id: 'partner',
    label: 'Regional/Local Partner',
    icon: MapPin,
    description: 'Partner at regional or local level',
    color: 'bg-blue-100 text-blue-700'
  },
  {
    id: 'advertiser',
    label: 'Advertiser',
    icon: Megaphone,
    description: 'Advertising opportunities',
    color: 'bg-amber-100 text-amber-700'
  },
  {
    id: 'ngo',
    label: 'NGO / Rescue Partner',
    icon: Heart,
    description: 'Non-profit partnership',
    color: 'bg-rose-100 text-rose-700'
  },
  {
    id: 'premium',
    label: 'Premium Membership',
    icon: Users,
    description: 'Pro or Premium member inquiry',
    color: 'bg-green-100 text-green-700'
  },
  {
    id: 'ombudsman',
    label: 'Ombudsman',
    icon: Shield,
    description: 'Dispute resolution & grievances',
    color: 'bg-slate-100 text-slate-700'
  },
  {
    id: 'general',
    label: 'General Inquiry',
    icon: Mail,
    description: 'Other questions or feedback',
    color: 'bg-gray-100 text-gray-700'
  }
];

const PARTNERSHIP_LEVELS = [
  { value: 'site-wide', label: 'Site-Wide (National/Global)' },
  { value: 'regional', label: 'Regional (State/Province)' },
  { value: 'local', label: 'Local (City/Community)' }
];

const ContactPage = () => {
  usePageTitle('Contact Us');
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || '';
  
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    inquiry_type: initialType,
    name: user?.display_name || user?.username || '',
    email: user?.email || '',
    organization: '',
    phone: '',
    subject: '',
    message: '',
    partnership_level: '',
    region: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.inquiry_type) {
      toast.error('Please select an inquiry type');
      return;
    }
    
    try {
      setLoading(true);
      
      await axios.post(`${API}/api/contact`, formData);
      
      setSubmitted(true);
      toast.success('Your inquiry has been submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit inquiry');
    } finally {
      setLoading(false);
    }
  };

  const selectedType = INQUIRY_TYPES.find(t => t.id === formData.inquiry_type);
  const showPartnershipFields = ['sponsor', 'partner', 'advertiser'].includes(formData.inquiry_type);

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16 max-w-2xl">
          <Card className="text-center">
            <CardContent className="p-12">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-heading font-bold mb-4">Thank You!</h1>
              <p className="text-muted-foreground mb-6">
                Your inquiry has been submitted successfully. We'll review it and get back to you 
                as soon as possible, typically within 1-2 business days.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/">
                  <Button variant="outline">
                    Return Home
                  </Button>
                </Link>
                <Button onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    inquiry_type: '',
                    name: user?.display_name || user?.username || '',
                    email: user?.email || '',
                    organization: '',
                    phone: '',
                    subject: '',
                    message: '',
                    partnership_level: '',
                    region: ''
                  });
                }}>
                  Submit Another Inquiry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Contact Us"
        description="Get in touch with All American Dog Club. Questions, feedback, partnership inquiries, and support."
        url="/contact"
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-12">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Home
          </Link>
          
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="w-8 h-8 text-primary" />
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            Get in Touch
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Interested in partnering, sponsoring, or have questions about premium membership? 
            We'd love to hear from you.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Inquiry Type Selection */}
          <div className="lg:col-span-1">
            <h2 className="font-semibold mb-4">What can we help with?</h2>
            <div className="space-y-2">
              {INQUIRY_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleChange('inquiry_type', type.id)}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                    formData.inquiry_type === type.id 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`inquiry-type-${type.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${type.color} flex items-center justify-center`}>
                      <type.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-muted-foreground">{type.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Direct email fallback — for users who prefer email over the form */}
            <Card className="mt-6" data-testid="direct-contact-card">
              <CardContent className="p-4">
                <div className="text-sm font-semibold mb-1">Prefer email?</div>
                <p className="text-xs text-muted-foreground mb-2">
                  Write to us directly and we&apos;ll route your message to the right team.
                </p>
                <a
                  href="mailto:support@americandogclub.com"
                  className="text-sm text-primary hover:underline break-all"
                  data-testid="direct-contact-email"
                >
                  support@americandogclub.com
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  {selectedType ? `${selectedType.label} Inquiry` : 'Contact Form'}
                </CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you shortly.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Contact Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Your Name *</Label>
                      <Input
                        id="name"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="organization">Organization / Company</Label>
                      <Input
                        id="organization"
                        placeholder="Acme Pet Supplies"
                        value={formData.organization}
                        onChange={(e) => handleChange('organization', e.target.value)}
                        data-testid="contact-organization"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>

                  {/* Partnership-specific fields */}
                  {showPartnershipFields && (
                    <div className="grid md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                      <div className="space-y-2">
                        <Label>Partnership Level</Label>
                        <Select
                          value={formData.partnership_level}
                          onValueChange={(value) => handleChange('partnership_level', value)}
                        >
                          <SelectTrigger data-testid="partnership-level">
                            <SelectValue placeholder="Select level..." />
                          </SelectTrigger>
                          <SelectContent>
                            {PARTNERSHIP_LEVELS.map((level) => (
                              <SelectItem key={level.value} value={level.value}>
                                {level.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="region">Region / Location</Label>
                        <Input
                          id="region"
                          placeholder="e.g., California, NYC, Southeast"
                          value={formData.region}
                          onChange={(e) => handleChange('region', e.target.value)}
                          data-testid="contact-region"
                        />
                      </div>
                    </div>
                  )}

                  {/* Subject & Message */}
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      placeholder="Brief description of your inquiry"
                      value={formData.subject}
                      onChange={(e) => handleChange('subject', e.target.value)}
                      required
                      data-testid="contact-subject"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us more about what you're looking for..."
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      required
                      rows={6}
                      data-testid="contact-message"
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      * Required fields
                    </p>
                    <Button 
                      type="submit" 
                      disabled={loading || !formData.inquiry_type}
                      className="gap-2"
                      data-testid="contact-submit"
                    >
                      {loading ? 'Sending...' : (
                        <>
                          <Send className="w-4 h-4" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Additional Info */}
            <div className="mt-8 p-6 bg-muted/30 rounded-lg">
              <h3 className="font-semibold mb-3">What Happens Next?</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                  We'll review your inquiry and respond within 1-2 business days
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                  For partnerships, we'll schedule a call to discuss opportunities
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                  Premium membership questions get priority response
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
